import { useEffect, useRef } from "react";
import { startRecording, StatsigSessionReplayPlugin, stopRecording } from "@statsig/session-replay";
import { StatsigAutoCapturePlugin } from "@statsig/web-analytics";

import { useCookieConsent } from "@/hooks/useCookieConsent";

import type { StatsigClient as StatsigJsClient } from "@statsig/js-client";
import type { StatsigUser } from "@statsig/react-bindings";

type AutoCaptureEvent = {
  metadata?: Record<string, unknown> | null;
};

type StatsigAutoCaptureInstance = {
  _disabledEvents?: Record<string, boolean>;
};

type StatsigGlobal = typeof globalThis & {
  __STATSIG__?: {
    acInstances?: Record<string, StatsigAutoCaptureInstance>;
  };
};

const DISABLED_AUTOCAPTURE_EVENTS = {
  "auto_capture::click": true,
  "auto_capture::copy": true,
  "auto_capture::dead_click": true,
  "auto_capture::error": true,
  "auto_capture::form_submit": true,
  "auto_capture::page_view": true,
  "auto_capture::page_view_end": true,
  "auto_capture::performance": true,
  "auto_capture::rage_click": true,
  "auto_capture::session_start": true,
  "auto_capture::web_vitals": true,
  "statsig::log_line": true,
};

export function useStatsigSessionReplayClient(
  adsManagerStatsigClient: StatsigJsClient | undefined,
  user: StatsigUser,
) {
  const { analyticsConsentEnabled } = useCookieConsent();
  const sessionReplayClientRef = useRef<StatsigJsClient | undefined>(undefined);
  const autoCaptureClientRef = useRef<StatsigJsClient | undefined>(undefined);
  const analyticsConsentEnabledRef = useRef(analyticsConsentEnabled);
  const previousAutoCaptureDisabledEventsRef = useRef<
    Record<string, Record<string, boolean> | undefined>
  >({});

  useEffect(() => {
    analyticsConsentEnabledRef.current = analyticsConsentEnabled;

    if (!adsManagerStatsigClient) {
      return;
    }

    if (analyticsConsentEnabled) {
      restoreStatsigAutoCapture(
        adsManagerStatsigClient,
        previousAutoCaptureDisabledEventsRef.current,
      );
    } else {
      disableStatsigAutoCapture(
        adsManagerStatsigClient,
        previousAutoCaptureDisabledEventsRef.current,
      );
    }
  }, [adsManagerStatsigClient, analyticsConsentEnabled]);

  useEffect(() => {
    if (!analyticsConsentEnabled || !adsManagerStatsigClient) {
      return;
    }

    const sdkKey = adsManagerStatsigClient.getContextHandle().sdkKey;
    if (sessionReplayClientRef.current === adsManagerStatsigClient) {
      startRecording(sdkKey);
    } else {
      new StatsigSessionReplayPlugin().bind(adsManagerStatsigClient);
      sessionReplayClientRef.current = adsManagerStatsigClient;
    }

    if (autoCaptureClientRef.current !== adsManagerStatsigClient) {
      new StatsigAutoCapturePlugin({
        eventFilterFunc: (event) =>
          filterStatsigAutoCaptureEvent(event, analyticsConsentEnabledRef.current),
      }).bind(adsManagerStatsigClient);
      autoCaptureClientRef.current = adsManagerStatsigClient;
    }

    adsManagerStatsigClient.updateUserSync(user);

    return () => {
      stopRecording(sdkKey);
    };
  }, [adsManagerStatsigClient, analyticsConsentEnabled, user]);
}

function filterStatsigAutoCaptureEvent(
  event: AutoCaptureEvent,
  analyticsConsentEnabled: boolean,
): boolean {
  if (!analyticsConsentEnabled) {
    return false;
  }

  scrubStatsigAutoCaptureMetadata(event);
  return true;
}

function scrubStatsigAutoCaptureMetadata(event: AutoCaptureEvent) {
  if (!event.metadata) {
    return;
  }

  delete event.metadata.content;
  delete event.metadata.selectedText;
}

function disableStatsigAutoCapture(
  statsigClient: StatsigJsClient,
  previousDisabledEventsBySdkKey: Record<string, Record<string, boolean> | undefined>,
) {
  const { instance, sdkKey } = getStatsigAutoCaptureInstance(statsigClient);
  if (!instance) {
    return;
  }

  if (!(sdkKey in previousDisabledEventsBySdkKey)) {
    previousDisabledEventsBySdkKey[sdkKey] = instance._disabledEvents;
  }

  // Statsig autocapture does not expose teardown. Its disabled-events map is checked
  // before getSession(), so this stops revoked-consent events from mutating session state.
  instance._disabledEvents = DISABLED_AUTOCAPTURE_EVENTS;
}

function restoreStatsigAutoCapture(
  statsigClient: StatsigJsClient,
  previousDisabledEventsBySdkKey: Record<string, Record<string, boolean> | undefined>,
) {
  const { instance, sdkKey } = getStatsigAutoCaptureInstance(statsigClient);
  if (!instance || !(sdkKey in previousDisabledEventsBySdkKey)) {
    return;
  }

  instance._disabledEvents = previousDisabledEventsBySdkKey[sdkKey];
  delete previousDisabledEventsBySdkKey[sdkKey];
}

function getStatsigAutoCaptureInstance(statsigClient: StatsigJsClient): {
  instance: StatsigAutoCaptureInstance | undefined;
  sdkKey: string;
} {
  const sdkKey = statsigClient.getContextHandle().sdkKey;
  return {
    instance: (globalThis as StatsigGlobal).__STATSIG__?.acInstances?.[sdkKey],
    sdkKey,
  };
}
